import { nanoid } from 'nanoid';
import camelCase from 'lodash.camelcase';
import mapKeys from 'lodash.mapkeys';
import snakeCase from 'lodash.snakecase';

/**
 * Recursivly execute function `func` for each object inside `obj`.
 * @param {Any} obj object of various structure
 * @param {function} func function with the interface:
 *                   func(<Object>) => <Object>
 * @returns {Any} original `obj` if `func` does not update it, otherwise
 *                modified `obj` if `func` updates it in any way
 */
export const deepApply = (obj, func) => {
    if (Array.isArray(obj)) {
        return obj.map(e => deepApply(e, func));
    }
    if (obj instanceof Object) {
        const appliedObj = func(obj);
        return Object.keys(appliedObj).reduce(
            (acc, key) => ({
                ...acc,
                [key]: deepApply(appliedObj[key], func),
            }),
            {},
        );
    }
    return obj;
};

/**
 * Transform input object's keys to snake_case, ommitting keys that are in UPPER_CASE.
 * @param {Object} obj object to transform
 * @returns {Object} obj object with transformed keys
 */
export const objToSnakeCase = obj =>
    mapKeys(obj, (value, key) => (key === key.toUpperCase() ? key : snakeCase(key)));

/**
 * Recursivly transforms all object's keys to sname_case, ommitting keys that are in UPPER_CASE
 * @param {Object} obj object to transform
 * @returns {Object} obj object with transformed keys
 */
export const deepSnakeCase = obj => deepApply(obj, objToSnakeCase);

/**
 * Transform all object's keys to camelCase, ommitting keys that are in UPPER_CASE.
 * @param {Object} obj object to transform
 * @returns {Object} obj object with transformed keys
 */
export const objToCamelCase = obj =>
    mapKeys(obj, (value, key) => (key === key.toUpperCase() ? key : camelCase(key)));

/**
 * Recursivly transforms all object's keys to camelCase, ommitting keys that are in UPPER_CASE
 * @param {Object} obj object to transform
 * @returns {Object} obj object with transformed keys
 */
export const deepCamelCase = obj => deepApply(obj, objToCamelCase);

/**
 * Add key id with generated string id to provided object
 * @param {Object} obj
 * @returns {Object} object with id
 */
export const addId = obj => ({
    ...obj,
    id: nanoid(),
});

/**
 * Recursivly go over object and add string id to each nested object
 * @param {Object} obj object to transform
 * @returns {Object} object with ids
 */
export const deepEntify = obj => deepApply(obj, addId);
